import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import {
	DatePicker as MuiDatePicker,
	DatePickerProps as MuiDatePickerProps,
	PickersActionBarProps,
	usePickerContext,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button, DialogActions, DialogActionsProps } from '@mui/material';
import classNames from 'classnames';
import useErrorScrollContext from '../../hooks/useErrorScrollContext';
import styles from './DatePicker.module.scss';

dayjs.extend( utc );

export type DatePickerActionListProps = DialogActionsProps & {
	clearable?: boolean;
	onClear?: () => void;
};

export const DatePickerActionList: React.FC<DatePickerActionListProps> = ( {
	clearable,
	onClear,
	...props
} ) => {
	const { cancelValueChanges, clearValue } = usePickerContext();

	return (
		<DialogActions { ...props }>
			{ clearable || clearable === undefined ? (
				<Button
					data-testid='clear-action-button'
					onClick={ () => {
						clearValue();
						onClear?.();
					} }
					variant='text'
				>
					Clear
				</Button>
			) : null }
			<Button
				data-testid='close-action-button'
				onClick={ () => {
					cancelValueChanges();
				} }
				variant='text'
			>
				Close
			</Button>
		</DialogActions>
	);
};

export const invalidDateString = 'Invalid date';

export type DatePickerProps = {
	errorText?: string;
	helperText?: string;
	value?: string | Date | null;
	onChange?: ( newValue: string | null ) => void;
	onAccept?: ( newValue: string | null ) => void;
	onBlur?: () => void;
	clearable?: boolean;
} & Omit<MuiDatePickerProps, 'value' | 'onChange' | 'onAccept'>;

export const DatePicker: React.FC<DatePickerProps> = ( {
	errorText,
	helperText,
	value,
	onChange,
	onAccept,
	onBlur,
	clearable,
	...datePickerProps
} ) => {
	const errorRef = useErrorScrollContext( !!errorText );
	const datePickerInputRef = useRef<HTMLInputElement>( null );
	const [ blurNextRenderCycle, setBlurNextRenderCycle ] = useState( false );
	const [ rawText, setRawText ] = useState<string | undefined>();
	/* Looks like the ony real way to clear a partially-filled in date (like 1-4 characters typed
	in) it to trigger a re-render with an input of null */
	const [ clearKey, setClearKey ] = useState<number>( 0 );
	const changeSourceRef = useRef<'external' | 'user' | null>( null );

	/* In order to determine if the prop value differs from the internal value, only considering
	date and excluding time, we use this helper function */
	const isSameDate = useCallback(
		( date1: Dayjs | null, date2: Dayjs | null ) =>
			// if both values are null then true
			!date1 && !date2
				? true
				: // of both values are dates then compare the dates as strings without time
				  ( date1 &&
						date2 &&
						date1.local().format( 'YYYY-MM-DD' ) ===
							date2.local().format( 'YYYY-MM-DD' ) ) ||
				  false,
		[]
	);

	/* Sanitize this prop value to one of 4 posabilities:
	- undefined
	- invalidDateString
	- a valid DayJS object
	- null */
	const externalValueAsDayjs = useMemo(
		() =>
			value === undefined
				? undefined
				: value === invalidDateString
					? invalidDateString
					: value && dayjs( value ).isValid()
						? dayjs( value )
						: value === null || value === ''
							? null
							: invalidDateString,
		[ value ]
	);

	/* move state down here so we can get the prop value into state, if applicable, one render
	cycle earlier than via a useEffect */
	const [ internalValue, setInternalValue ] = useState<Dayjs | null | undefined>(
		externalValueAsDayjs !== undefined &&
			externalValueAsDayjs !== invalidDateString
			? externalValueAsDayjs
			: undefined
	);

	const setInternalValueFromUser = useCallback( ( newValue: Dayjs | null ) => {
		changeSourceRef.current = 'user';
		setInternalValue( newValue );
	}, [] );

	// set the initial value from the prop value, but only if it's changing to avoid render loops
	useEffect( () => {
		if (
			externalValueAsDayjs !== undefined &&
			externalValueAsDayjs !== invalidDateString
		) {
			changeSourceRef.current = 'external';
			setInternalValue( ( prevState ) =>
				prevState === undefined
					? externalValueAsDayjs
					: isSameDate( prevState, externalValueAsDayjs )
						? prevState
						: externalValueAsDayjs
			);
		}
	}, [
		externalValueAsDayjs,
		isSameDate,
		changeSourceRef
	] );

	/* this keeps the day from jumping backwards as you type
	For example, in the component test, without this, typing 12202008 becomes 12/19/2008
	This was a bug Sari reported */
	useEffect( () => {
		if ( changeSourceRef.current !== 'user' ) {
			return;
		}

		if (
			internalValue !== undefined &&
			( externalValueAsDayjs === undefined ||
				externalValueAsDayjs === invalidDateString ||
				!isSameDate( internalValue, externalValueAsDayjs ) )
		) {
			/* when you type "1220202" and just feed the value out of this component that
			ends up as month=11, day=1, year=0202, so we only pass out values when years are
			greater than 1000 */
			if (
				internalValue &&
				internalValue.isValid() &&
				internalValue.year() > 999
			) {
				const dateStringValue = internalValue.local().format();
				onChange?.( dateStringValue );
				onAccept?.( dateStringValue );
			} else if ( internalValue === null && rawText === '' ) {
				/* But we also allow clearing the value, only if the raw text is also empty so we
				don't bounce between Invalid Date and null */
				onChange?.( null );
				onAccept?.( null );
			}
		}

		changeSourceRef.current = null;
	}, [
		externalValueAsDayjs,
		internalValue,
		isSameDate,
		onChange,
		onAccept,
		rawText,
		changeSourceRef,
	] );

	/* In order to tell what is actually typed to meet expectations on validation, because
	MUI only fires onChange after 5 characters are typed, we need to interact with the DOM
	directly to see typed characters 1-4 */
	const input = datePickerInputRef?.current;
	useEffect( () => {
		if ( input ) {
			Object.defineProperty( input, 'value', {
				set: function( t ) {
					input.setAttribute( 'value', t );
					setRawText( t );
				},
				get: function() {
					return input.getAttribute( 'value' );
				},
			} );
		}
	}, [ input ] );

	/* because when we clear we have to wait one render cycle for the data to get updated
	in state before we can validate, we use this useEffect to do the blurring on clear */
	useEffect( () => {
		if ( value === null && blurNextRenderCycle ) {
			onBlur?.();
			setBlurNextRenderCycle( false );
		}
	}, [
		value,
		onBlur,
		blurNextRenderCycle
	] );

	// This lets us clear the partially-typed value (1-4 characters typed)
	const clearHiddenInputValue = useCallback( () => {
		setInternalValueFromUser( null );
		setRawText( '' );
		setClearKey( ( prevKey ) => prevKey + 1 );
		setBlurNextRenderCycle( true );
	}, [ setInternalValueFromUser ] );

	// Here we report that the date is invalid, and this should only fire if the other useEffect doesn't
	useEffect( () => {
		if (
			rawText &&
			rawText !== 'MM/DD/YYYY' &&
			!dayjs( rawText ).isValid() &&
			externalValueAsDayjs !== invalidDateString
		) {
			onChange?.( invalidDateString );
		} else if ( rawText === '' && externalValueAsDayjs === invalidDateString ) {
			onChange?.( null );
		}
	}, [
		rawText,
		onChange,
		externalValueAsDayjs
	] );

	return (
		<LocalizationProvider dateAdapter={ AdapterDayjs }>
			<MuiDatePicker
				{ ...datePickerProps }
				inputRef={ datePickerInputRef }
				ref={ errorRef }
				key={ clearKey }
				desktopModeMediaQuery='(hover: hover) and (pointer: fine), (min-width: 960px)'
				value={ internalValue || null }
				onChange={ ( djs ) => setInternalValueFromUser( djs ) }
				closeOnSelect={ true }
				slots={ {
					...datePickerProps?.slots,
					actionBar: ( props: PickersActionBarProps ) =>
						DatePickerActionList( {
							...props,
							onClear: clearHiddenInputValue,
							clearable,
						} ),
				} }
				slotProps={ {
					...datePickerProps.slotProps,
					desktopPaper: ( ownerState ) => {
						const desktopPaperProps =
							typeof datePickerProps.slotProps?.desktopPaper === 'function'
								? datePickerProps.slotProps.desktopPaper( ownerState )
								: datePickerProps.slotProps?.desktopPaper;

						return {
							...desktopPaperProps,
							className: classNames(
								styles.singleDatePicker,
								desktopPaperProps?.className
							),
						};
					},
					mobilePaper: {
						...datePickerProps.slotProps?.mobilePaper,
						className: classNames(
							styles.singleDatePicker,
							datePickerProps.slotProps?.mobilePaper?.className
						),
					},
					textField: ( ownerState ) => {
						const textFieldProps =
							typeof datePickerProps.slotProps?.textField === 'function'
								? datePickerProps.slotProps.textField( ownerState )
								: datePickerProps.slotProps?.textField;

						return {
							...textFieldProps,
							className: classNames(
								styles.datePickerField,
								textFieldProps?.className
							),
							helperText: errorText || helperText,
							error: !!errorText,
						};
					},
					field: {
						...datePickerProps.slotProps?.field,
						onBlur, // looks like datePickerProps.slotProps?.textField doesn't have an onBlur but field does
					},
				} }
			/>
		</LocalizationProvider>
	);
};
